import {
  Avatar,
  Button,
  Card,
  CardContent,
  Input,
  Select,
  SelectItem,
  SelectTriggerAction,
  Tabs,
  TabsList,
  TabsTrigger,
  ToggleWithLabel,
  Tooltip,
  Typography,
  useLocaleFormat,
} from '@eye-able/ui';
import { Download, X } from 'lucide-react';
import type { ChangeEvent, ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { FormDomainSelect } from '@components/FormDomainSelect';
import LoadingSpinner from '@components/LoadingSpinner';
import { getInitials } from '@helpers/functions';
import { resolveTeamColor } from '@pages/organisation/teams/teamColors';
import {
  useFetchGovernanceIssueGroups,
  type GovernanceIssuesGroupBy,
} from '@http/queries/useFetchGovernanceIssueGroups';
import {
  useFetchGovernanceIssues,
  type GovernanceIssueResponse,
  type GovernanceIssuePriority,
  type GovernanceIssueStatus,
  type GovernanceIssuesDateRange,
  type GovernanceIssuesFilters,
} from '@http/queries/useFetchGovernanceIssues';
import { useFetchGovernanceIssuePolicies } from '@http/queries/useFetchGovernanceIssuePolicies';
import { useExportGovernanceIssues } from '@http/queries/useExportGovernanceIssues';
import {
  type UpdatableGovernanceIssueStatus,
  useUpdateGovernanceIssueStatus,
} from '@http/queries/useUpdateGovernanceIssueStatus';
import { useFetchTeams } from '@http/queries';

import {
  DATE_OPTIONS,
  GROUP_BY_OPTIONS,
  PRIORITY_OPTIONS,
  STATUS_LABELS,
  STATUS_OPTIONS,
  type GroupByKey,
} from './constants';
import FilterCombobox from './FilterCombobox';
import { FilteredIssuesPlaceholder } from './FilteredIssuesPlaceholder';
import GroupedIssueList from './GroupedIssueList';
import IssueCard from './IssueCard';
import { NoIssuesPlaceholder } from './NoIssuesPlaceholder';
import PrioritySignal from '../PrioritySignal';
import { getPriorityTranslationKey } from '../priority';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import {
  useCanEditGovernanceDomain,
  useGovernanceAccess,
} from '@/hooks/useGovernanceAccess';
import { ReadOnlyBanner } from '@/components/access/ReadOnlyBanner';
import { GovernanceAccessBoundary } from '../GovernanceAccessBoundary';
import triggerDownload from '@/helpers/triggerDownload';
import { T } from '@/i18n/T';

export function GovernanceIssuesContent({
  header,
  navigation,
}: {
  header?: ReactNode;
  navigation?: ReactNode;
}) {
  const { t } = useInlineTranslation();
  const { formatNumber } = useLocaleFormat();
  const { canEdit } = useGovernanceAccess();
  const canEditIssueDomain = useCanEditGovernanceDomain();
  const [searchParams] = useSearchParams();
  const policyIdFromUrl = Number(searchParams.get('policyId'));
  const [search, setSearch] = useState('');
  const [policyId, setPolicyId] = useState<number | null>(() =>
    Number.isInteger(policyIdFromUrl) && policyIdFromUrl > 0
      ? policyIdFromUrl
      : null,
  );
  const [teamId, setTeamId] = useState<number | null>(null);
  const [priority, setPriority] = useState<GovernanceIssuePriority | null>(
    null,
  );
  const [status, setStatus] = useState<GovernanceIssueStatus>('open');
  const [domainIds, setDomainIds] = useState<number[]>([]);
  const [date, setDate] = useState<GovernanceIssuesDateRange | null>('90d');
  const [hasScreenshot, setHasScreenshot] = useState(false);
  const [groupBy, setGroupBy] = useState<GroupByKey>('none');
  const { data: policies = [] } = useFetchGovernanceIssuePolicies();
  const { data: teams = [] } = useFetchTeams(undefined, { enabled: true });
  const selectedPolicy =
    policies.find((policy) => policy.id === policyId) ?? null;
  const policyViolationLabels = useMemo(
    () => new Map(policies.map((policy) => [policy.id, policy.violationLabel])),
    [policies],
  );

  const issueFilters = useMemo<GovernanceIssuesFilters>(
    () => ({
      search,
      policyId: policyId ?? undefined,
      teamId: teamId ?? undefined,
      priority: priority ?? undefined,
      status,
      domainIds,
      dateRange: date ?? undefined,
      hasScreenshot,
    }),
    [
      date,
      domainIds,
      hasScreenshot,
      policyId,
      priority,
      search,
      status,
      teamId,
    ],
  );
  const activeGroupBy: GovernanceIssuesGroupBy | null =
    groupBy === 'none' ? null : groupBy;
  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useFetchGovernanceIssues(issueFilters, activeGroupBy === null);
  const {
    data: issueGroups = [],
    isLoading: isLoadingGroups,
    isError: isGroupsError,
  } = useFetchGovernanceIssueGroups(activeGroupBy, issueFilters);
  const updateIssueStatus = useUpdateGovernanceIssueStatus();
  const exportIssues = useExportGovernanceIssues();
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const issues = useMemo(
    () => data?.pages.flatMap((page) => page.data) ?? [],
    [data],
  );
  const totalIssues =
    activeGroupBy === null
      ? (data?.pages[0]?.total ?? issues.length)
      : issueGroups.reduce((total, group) => total + group.count, 0);

  const hasActiveFilters =
    search !== '' ||
    policyId !== null ||
    teamId !== null ||
    priority !== null ||
    status !== 'open' ||
    domainIds.length > 0 ||
    hasScreenshot;

  const pendingUpdate = updateIssueStatus.isPending
    ? updateIssueStatus.variables
    : null;

  function handleStatusChange(
    issue: GovernanceIssueResponse,
    status: UpdatableGovernanceIssueStatus,
  ) {
    if (!canEditIssueDomain(issue.auditEntryId) || issue.status === status) {
      return;
    }

    updateIssueStatus.mutate({ id: issue.id, status });
  }

  function handleSearchChange(event: ChangeEvent<HTMLInputElement>) {
    setSearch(event.target.value);
  }

  function handleResetFilters() {
    setSearch('');
    setPolicyId(null);
    setTeamId(null);
    setPriority(null);
    setStatus('open');
    setDomainIds([]);
    setDate('90d');
    setHasScreenshot(false);
  }

  function handleExport() {
    exportIssues.mutate(issueFilters, {
      onSuccess: (file) => triggerDownload(file, 'governance-issues.csv'),
    });
  }

  useEffect(() => {
    const target = loadMoreRef.current;
    if (activeGroupBy !== null || !target || !hasNextPage) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { rootMargin: '300px 0px' },
    );

    observer.observe(target);

    return () => observer.disconnect();
  }, [activeGroupBy, fetchNextPage, hasNextPage, isFetchingNextPage]);

  return (
    <div className='eye:flex eye:flex-col eye:gap-m'>
      <div className='eye:flex eye:flex-col eye:gap-s'>
        <div className='eye:flex eye:items-start eye:justify-between eye:gap-s eye:max-lg:flex-col-reverse'>
          {header ?? (
            <Typography tag='h1' variant='headline-xl'>
              <T k='issues' />
            </Typography>
          )}
          <div className='eye:w-[350px] eye:shrink-0 eye:max-lg:self-end eye:max-sm:w-full'>
            <FormDomainSelect
              label={t('domain')}
              placeholder={t('all')}
              value={domainIds}
              onValueChange={setDomainIds}
              slotProps={{
                trigger: { className: 'eye:bg-white' },
                label: { className: 'eye:bg-label-gradient-grey' },
              }}
            />
          </div>
        </div>
        {navigation}
      </div>
      <div className='eye:flex eye:flex-col eye:gap-xs'>
        <div>
          <Typography className='eye:mt-5xs eye:text-grey-120'>
            {totalIssues === 0
              ? t(hasActiveFilters ? 'noFilteredIssues' : 'noIssues')
              : t('issuesFound', {
                  count: totalIssues,
                  formattedCount: formatNumber(totalIssues),
                })}
          </Typography>
        </div>

        <ReadOnlyBanner show={!canEdit} />

        <div className='eye:flex eye:flex-wrap eye:items-center eye:gap-xs'>
          <div className='eye:w-full eye:max-w-[400px]'>
            <Input
              label={t('searchIssues')}
              placeholder={t('searchIssuesPlaceholder')}
              className='eye:bg-white'
              labelProps={{ className: 'eye:bg-label-gradient-grey' }}
              value={search}
              onChange={handleSearchChange}
            />
          </div>
          <FilterCombobox
            label={t('policy')}
            items={policies}
            value={selectedPolicy}
            onValueChange={(nextPolicy) => setPolicyId(nextPolicy?.id ?? null)}
            itemToStringLabel={(item) => item.name}
            itemToStringValue={(item) => String(item.id)}
          />
          {teams.length > 0 ? (
            <div className='eye:relative eye:w-[220px]'>
              <Select
                label={t('team')}
                placeholder={t('allTeams')}
                value={teamId?.toString() ?? ''}
                onValueChange={(value) => setTeamId(Number(value))}
                slotProps={{
                  trigger: {
                    className: 'eye:bg-white',
                    customTriggers:
                      teamId !== null
                        ? [
                            {
                              key: 'clear-team',
                              render: () => (
                                <SelectTriggerAction
                                  size='icon-xs'
                                  aria-label={t('clearSelectedTeam')}
                                  onClick={() => setTeamId(null)}
                                >
                                  <X aria-hidden />
                                </SelectTriggerAction>
                              ),
                            },
                          ]
                        : undefined,
                  },
                  label: { className: 'eye:bg-label-gradient-grey' },
                }}
              >
                {teams.map((team) => (
                  <SelectItem key={team.id} value={String(team.id)}>
                    <span className='eye:flex eye:items-center eye:gap-2xs'>
                      <Avatar
                        style={{
                          backgroundColor: resolveTeamColor(team.color),
                        }}
                        className='eye:size-[24px] eye:shrink-0 eye:rounded-md eye:font-semibold eye:text-white'
                      >
                        {getInitials(team.name)}
                      </Avatar>
                      {team.name}
                    </span>
                  </SelectItem>
                ))}
              </Select>
            </div>
          ) : null}
          <FilterCombobox
            label={t('priority')}
            items={PRIORITY_OPTIONS}
            value={priority}
            onValueChange={setPriority}
            itemToStringLabel={(item) => t(getPriorityTranslationKey(item))}
            renderItemLabel={(item) => (
              <span className='eye:flex eye:items-center eye:gap-4xs'>
                <PrioritySignal priority={item} className='eye:size-[16px]' />
                {t(getPriorityTranslationKey(item))}
              </span>
            )}
            renderValue={(item) => (
              <span className='eye:flex eye:items-center eye:gap-4xs'>
                <PrioritySignal priority={item} className='eye:size-[16px]' />
                {t(getPriorityTranslationKey(item))}
              </span>
            )}
          />
          <FilterCombobox
            label={t('status')}
            items={STATUS_OPTIONS}
            value={status}
            onValueChange={(nextStatus) => {
              if (nextStatus) {
                setStatus(nextStatus);
              }
            }}
            itemToStringLabel={(item) => t(STATUS_LABELS[item])}
            clearable={false}
          />
          <div className='eye:w-[220px]'>
            <Select
              label={t('date')}
              value={date ?? undefined}
              onValueChange={(value) =>
                setDate(value as GovernanceIssuesDateRange)
              }
              slotProps={{
                trigger: { className: 'eye:bg-white' },
                label: { className: 'eye:bg-label-gradient-grey' },
              }}
            >
              {DATE_OPTIONS.map((option) => (
                <SelectItem key={option} value={option}>
                  {t(`dateRange${option}`)}
                </SelectItem>
              ))}
            </Select>
          </div>
          <ToggleWithLabel
            label={t('hasScreenshot')}
            checked={hasScreenshot}
            onCheckedChange={setHasScreenshot}
          />
        </div>

        <div className='eye:flex eye:flex-wrap eye:items-center eye:justify-between eye:gap-xs'>
          <div className='eye:flex eye:flex-wrap eye:items-center eye:gap-2xs'>
            <Typography tag='span' className='eye:body-s eye:text-grey-120'>
              <T k='groupBy' />
            </Typography>
            <Tabs
              value={groupBy}
              onValueChange={(value) => setGroupBy(value as GroupByKey)}
            >
              <TabsList>
                {GROUP_BY_OPTIONS.map((option) => (
                  <TabsTrigger key={option.key} value={option.key}>
                    {t(option.labelKey)}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
          <Tooltip content={t('exportCsv')}>
            <Button
              variant='ghost'
              size='icon'
              aria-label={t('exportCsv')}
              onClick={handleExport}
              disabled={exportIssues.isPending}
            >
              <Download aria-hidden />
            </Button>
          </Tooltip>
        </div>

        {(activeGroupBy === null && isLoading) ||
        (activeGroupBy !== null && isLoadingGroups) ? (
          <LoadingSpinner />
        ) : (activeGroupBy === null && isError) ||
          (activeGroupBy !== null && isGroupsError) ? (
          <Card>
            <CardContent className='eye:p-s'>
              <Typography className='eye:text-grey-120'>
                <T k='couldNotLoadIssues' />
              </Typography>
            </CardContent>
          </Card>
        ) : totalIssues === 0 ? (
          hasActiveFilters ? (
            <FilteredIssuesPlaceholder onResetFilters={handleResetFilters} />
          ) : (
            <NoIssuesPlaceholder />
          )
        ) : groupBy === 'none' ? (
          <div className='eye:flex eye:flex-col eye:gap-2xs'>
            {issues.map((issue) => (
              <IssueCard
                key={issue.id}
                issue={issue}
                policyViolationLabel={policyViolationLabels.get(issue.policyId)}
                pendingStatus={
                  pendingUpdate?.id === issue.id ? pendingUpdate.status : null
                }
                onStatusChange={handleStatusChange}
              />
            ))}
          </div>
        ) : (
          <GroupedIssueList
            // Group keys are only unique within one dimension, so remount to
            // reset accordion state when the grouping dimension changes.
            key={activeGroupBy}
            groups={issueGroups}
            groupBy={activeGroupBy!}
            filters={issueFilters}
            pendingUpdate={pendingUpdate}
            onStatusChange={handleStatusChange}
            policyViolationLabels={policyViolationLabels}
          />
        )}

        {activeGroupBy === null && hasNextPage ? (
          <div ref={loadMoreRef} className='eye:h-px' />
        ) : null}
        {activeGroupBy === null && isFetchingNextPage ? (
          <LoadingSpinner />
        ) : null}
      </div>
    </div>
  );
}

function GovernanceIssues() {
  return (
    <GovernanceAccessBoundary>
      <GovernanceIssuesContent />
    </GovernanceAccessBoundary>
  );
}

export { GovernanceIssues as Component };
export default GovernanceIssues;
